import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { TwilioConnection } from "@tw-pulse/shared";
import { api } from "./api";
import { useAuth } from "./auth";

/**
 * Global account-context selector.
 *
 * Every multi-tenant observability product has one — Datadog filters by
 * service/team, Stripe Dashboard switches accounts. For TwilioPulse, the
 * user picks which connected Twilio accounts to scope insights to.
 *
 * The selection is per-user (keyed by userId in localStorage), survives
 * reloads, and is consumed by every data hook that fetches tenant-scoped
 * telemetry. When a new connection arrives:
 *   - mode "all"    → it's auto-included
 *   - mode "subset" → user must explicitly add it
 */

export type ConnectionSelection = { mode: "all" } | { mode: "subset"; ids: string[] };

interface ContextValue {
  status: "loading" | "ready" | "error";
  connections: TwilioConnection[];
  selection: ConnectionSelection;
  setSelection: (s: ConnectionSelection) => void;
  /** Resolved IDs given the current selection + the live connections list. */
  selectedIds: string[];
  /** Convenience: true when mode is "all". */
  allSelected: boolean;
  /** Refetch the connections list (after add / delete / status change). */
  refresh: () => Promise<void>;
}

const ConnectionContext = createContext<ContextValue | null>(null);

const STORAGE_PREFIX = "tw-pulse.connection-selection.";

function storageKey(userId: string): string {
  return STORAGE_PREFIX + userId;
}

function loadSelection(userId: string): ConnectionSelection {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return { mode: "all" };
    const parsed = JSON.parse(raw) as ConnectionSelection;
    if (parsed.mode === "all") return { mode: "all" };
    if (parsed.mode === "subset" && Array.isArray(parsed.ids)) {
      return { mode: "subset", ids: parsed.ids };
    }
    return { mode: "all" };
  } catch {
    return { mode: "all" };
  }
}

function saveSelection(userId: string, selection: ConnectionSelection): void {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(selection));
  } catch {
    // localStorage may be disabled — fail silent, behavior just stops persisting.
  }
}

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const { me, status: authStatus } = useAuth();
  const userId = me?.user.id;

  const [connections, setConnections] = useState<TwilioConnection[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [selection, setSelectionState] = useState<ConnectionSelection>({ mode: "all" });

  // Initial load: pull saved selection once we know who the user is.
  useEffect(() => {
    if (authStatus !== "authenticated" || !userId) return;
    setSelectionState(loadSelection(userId));
  }, [authStatus, userId]);

  const refresh = useCallback(async () => {
    if (authStatus !== "authenticated") return;
    setStatus("loading");
    try {
      const r = await api.twilio.list();
      setConnections(r.connections);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [authStatus]);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    void refresh();
  }, [authStatus, refresh]);

  // Reconcile selection when connections list changes — drop IDs that no
  // longer exist (deleted/no-longer-visible connections).
  useEffect(() => {
    if (selection.mode !== "subset") return;
    if (status !== "ready") return;
    const live = new Set(connections.map((c) => c.id));
    const filtered = selection.ids.filter((id) => live.has(id));
    if (filtered.length !== selection.ids.length) {
      const next: ConnectionSelection =
        filtered.length === 0 ? { mode: "all" } : { mode: "subset", ids: filtered };
      setSelectionState(next);
      if (userId) saveSelection(userId, next);
    }
  }, [connections, selection, userId, status]);

  const setSelection = useCallback(
    (next: ConnectionSelection) => {
      setSelectionState(next);
      if (userId) saveSelection(userId, next);
    },
    [userId],
  );

  const selectedIds = useMemo(() => {
    if (selection.mode === "all") return connections.map((c) => c.id);
    return selection.ids;
  }, [selection, connections]);

  const value = useMemo<ContextValue>(
    () => ({
      status,
      connections,
      selection,
      setSelection,
      selectedIds,
      allSelected: selection.mode === "all",
      refresh,
    }),
    [status, connections, selection, setSelection, selectedIds, refresh],
  );

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

export function useConnectionContext(): ContextValue {
  const ctx = useContext(ConnectionContext);
  if (!ctx) throw new Error("useConnectionContext must be used inside <ConnectionProvider>");
  return ctx;
}
